import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import { FilePlus2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone/.';

import { useTheme } from '@/app/ThemeContext';
import { BaseTag, Tabs } from '@/components/chariot-ui';
import { Button } from '@/components/ui/button';
import { Dropzone, Files } from '@/components/Dropzone';
import { Input } from '@/components/form/Input';
import { getFormValues, Inputs, Values } from '@/components/form/Inputs';
import { Link } from '@/components/Link';
import { Modal } from '@/components/Modal';
import { Container } from '@/components/legacy-ui';
import { useModifyAccount } from '@/hooks';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useUploadFile } from '@/hooks/useFiles';
import { useGraphQuery } from '@/hooks/useGraphQuery';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import {
  IntegrationValidationError,
  ValidateIntegrationErrorResponse,
  ValidationCredentials,
} from '@/hooks/useValidateIntegration';
import { JiraProjectSearch } from '@/sections/JiraProjectSearch';
import { TransitionsPanel } from '@/sections/TransitionsPanel';
import {
  Account,
  IntegrationInput,
  IntegrationMeta,
  JiraProject,
  JiraTemplate,
  JiraTransitionConfig,
  LinkAccount,
} from '@/types';
import { cn } from '@/utils/classname';
import { generateUuid } from '@/utils/uuid.util';

// Map vulnerability scanners to their accepted file types
const VULNERABILITY_SCANNERS: {
  [key: string]: { accept: Accept };
} = {
  nessus: { accept: { 'application/.nessus': ['.nessus'] } },
  qualys: { accept: { 'text/csv': ['.csv'] } },
  insightvm: { accept: { 'application/xml': ['.xml'] } },
  sharphound: { accept: { 'application/zip': ['.zip'] } },
  cisachvs: { accept: { 'text/csv': ['.csv'] } },
  cisachwas: { accept: { 'text/csv': ['.csv'] } },
  nsacsaasasm: { accept: { 'text/csv': ['.csv'] } },
} as const;

type Step = 'setup' | 'configuration' | 'transitions' | 'permissions';

interface SetupModalProps {
  step?: Step;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedIntegration?: Account;
  formData: Values;
  integration?: IntegrationMeta;
  hideAddLater?: boolean;
  parseFormData?: (formData: Values) => Values;
  onSwitchIntegration?: (integration: IntegrationMeta) => void;
}

const configurationTabs = [
  { label: 'Configuration', value: 'configuration' },
  { label: 'Transitions', value: 'transitions' },
  { label: 'Permissions', value: 'permissions' },
];

const SetupModal: React.FC<SetupModalProps> = ({
  step: stepProps = 'setup',
  isOpen,
  onClose,
  onComplete,
  selectedIntegration: connectedIntegration,
  formData: formDataParent,
  integration: integrationMeta,
  parseFormData,
  onSwitchIntegration,
}) => {
  const integration: IntegrationMeta | undefined =
    integrationMeta ||
    (connectedIntegration
      ? AllIntegrations[
          connectedIntegration.member as keyof typeof AllIntegrations
        ]
      : undefined);

  const defaultSetupValues =
    integration?.inputs && formDataParent
      ? { ...getFormValues(integration.inputs), ...formDataParent }
      : undefined;

  const defaultConfigValuesWithoutId = integration?.configuration?.inputs
    ? {
        ...getFormValues(integration?.configuration?.inputs),
        ...formDataParent,
      }
    : undefined;

  const defaultConfigValues = defaultConfigValuesWithoutId
    ? {
        ...defaultConfigValuesWithoutId,
        id: defaultConfigValuesWithoutId.id || generateUuid(),
      }
    : undefined;

  const isVulnerabilityScanner =
    integration?.id && integration.id in VULNERABILITY_SCANNERS;
  const scannerConfig = isVulnerabilityScanner
    ? VULNERABILITY_SCANNERS[
        integration.id as keyof typeof VULNERABILITY_SCANNERS
      ]
    : null;
  const key = connectedIntegration?.key;

  // Mutations
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');
  const { mutateAsync: unLink, status: unLinkStatus } =
    useModifyAccount('unlink');
  const { mutateAsync: updateAccount, status: updateAccountStatus } =
    useModifyAccount('update');
  const { mutateAsync: uploadFile } = useUploadFile();
  const integrationSettings = useIntegrationSettings(
    integration?.overrideMemberName || integration?.id,
    connectedIntegration?.value
  );
  const {
    projects,
    onValidateIntegration,
    templatesError,
    setTemplatesError,
    getIntegrationProjects,
    searchProjects,
    setProjects,
  } = integrationSettings;
  const isValidating = integrationSettings.status?.isValidating ?? false;
  const [validationErrors, setValidationErrors] = useState<
    IntegrationValidationError[]
  >([]);
  const { currentTheme } = useTheme();
  const { track } = useAnalytics();

  // Check if we need to fetch assets for dynamic options (e.g., Azure assets for Azure DevOps)
  const assetClassForOptions = useMemo(() => {
    return integration?.inputs?.find(input => input.optionsFromAccounts)
      ?.optionsFromAccounts;
  }, [integration]);

  // Fetch assets with the desired class for dropdown options
  const { data: assetsData, isLoading: isLoadingAssets } = useGraphQuery({
    query: {
      node: {
        labels: ['Asset'],
        filters: assetClassForOptions
          ? [
              { field: 'class', operator: '=', value: assetClassForOptions },
              { field: 'source', operator: '=', value: 'account' },
            ]
          : [],
      },
      limit: 100,
    },
    enabled: !!assetClassForOptions,
  });

  // Process inputs to inject dynamic options from assets
  const processedInputs = useMemo(() => {
    if (!integration?.inputs) return [];

    return integration.inputs.map(input => {
      if (input.optionsFromAccounts) {
        // Still loading assets
        if (isLoadingAssets) {
          return {
            ...input,
            options: [],
            placeholder: 'Loading integrations...',
            disabled: true,
          };
        }

        const assets = assetsData?.assets || [];

        // If no assets found, return input with empty options and a helpful message
        if (assets.length === 0) {
          return {
            ...input,
            options: [],
            placeholder: `No ${input.optionsFromAccounts} integrations found`,
            disabled: true,
          };
        }

        // Map assets to select options using asset key
        const options = assets.map(asset => ({
          label: asset.name || asset.key,
          value: asset.key,
        }));

        // Auto-select if only one option
        const defaultValue = assets.length === 1 ? assets[0].key : input.value;

        return {
          ...input,
          options,
          value: defaultValue,
        };
      }
      return input;
    });
  }, [integration, assetsData, isLoadingAssets]);

  // Local States
  const [step, setStep] = useState<
    'setup' | 'configuration' | 'transitions' | 'permissions'
  >(stepProps);
  const [setupData, setSetupData] = useState<Values>(defaultSetupValues || {});
  const [prevIntegrationId, setPrevIntegrationId] = useState(integration?.id);

  // Reset setupData when integration changes (e.g., switching from azuredevops to azuredevops-pat)
  // to prevent stale fields (like parentKey) from leaking into the new form submission.
  // Uses synchronous state reset pattern (not useEffect) to ensure setupData is clean
  // before the Inputs component renders with the new integration's fields.
  if (integration?.id !== prevIntegrationId) {
    setPrevIntegrationId(integration?.id);
    const newDefaults = integration?.inputs
      ? getFormValues(integration.inputs)
      : {};
    setSetupData(newDefaults);
  }

  // Update setupData when processedInputs has auto-selected values (e.g., single Azure account)
  // Using queueMicrotask to avoid synchronous setState in effect body
  useEffect(() => {
    queueMicrotask(() => {
      setSetupData(prev => {
        const autoSelectedValues = processedInputs.reduce((acc, input) => {
          // Auto-select if input has optionsFromAccounts, has a value, and setupData doesn't have a value yet
          const currentValue = prev[input.name];
          const shouldAutoSelect =
            input.optionsFromAccounts &&
            input.value &&
            (currentValue === '' || currentValue === undefined);
          if (shouldAutoSelect) {
            return { ...acc, [input.name]: input.value };
          }
          return acc;
        }, {} as Values);

        if (Object.keys(autoSelectedValues).length > 0) {
          return { ...prev, ...autoSelectedValues };
        }
        return prev;
      });
    });
  }, [processedInputs]);
  const [configData, setConfigData] = useState<Values>(
    defaultConfigValues || {}
  );
  const [transitionsData, setTransitionsData] = useState<JiraTransitionConfig>(
    () => {
      // Initialize from existing template when editing
      const templateId = defaultConfigValues?.id;
      const existingTemplates = connectedIntegration?.settings?.templates ?? [];
      const existingTemplate = existingTemplates.find(
        t => t.id === templateId
      ) as JiraTemplate | undefined;

      return (
        existingTemplate?.transitions ?? {
          openToClose: [],
          closeToOpen: [],
        }
      );
    }
  );

  // Memoized values
  const isEditingMode = useMemo(() => {
    return !!integration?.configuration && !!key;
  }, [integration?.configuration, key]);

  const {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  } = useFormValidation();

  // Wrapper for searchProjects that handles both new and existing integrations
  const handleSearchProjects = useCallback(
    async (searchTerm: string) => {
      // For existing integrations (has key), searchProjects uses the account key
      // For new integrations (no key), pass the credentials from setupData
      const config = key ? undefined : convertObjectValuesToStrings(setupData);
      return searchProjects(searchTerm, config);
    },
    [key, setupData, searchProjects]
  );

  useEffect(() => {
    if (step === 'configuration') {
      if (key) {
        // Existing integration - load projects using account key
        getIntegrationProjects();
      } else if (Object.keys(setupData).length > 0) {
        // New integration - load projects using credentials
        handleSearchProjects('').then(initialProjects => {
          setProjects(initialProjects);
        });
      }
    }
  }, [
    key,
    step,
    getIntegrationProjects,
    handleSearchProjects,
    setupData,
    setProjects,
  ]);

  const handleSetupInputsChange = useCallback(
    (newValues: Values) => {
      setSetupData(newValues);
      setValidationErrors([]); // Clear validation errors on input change

      // If user has attempted to submit, validate on change
      if (hasAttemptedSubmit) {
        setFormErrors(prevErrors => {
          const newErrors: Record<string, boolean> = { ...prevErrors };
          Object.keys(newValues).forEach(key => {
            if (newValues[key]) {
              delete newErrors[key];
            }
          });
          return newErrors;
        });
      }
    },
    [hasAttemptedSubmit, setFormErrors]
  );

  const handleConfigDataChange = (newValues: Values) => {
    setFormErrors({});
    setTemplatesError({});
    setValidationErrors([]); // Clear validation errors on input change
    setConfigData(currentValues => {
      // Preserve projectKey if newValues has empty/default value (from Inputs component)
      // Only update projectKey if newValues has a non-empty value (from JiraProjectSearch)
      const projectKey = newValues.projectKey || currentValues.projectKey;
      const merged = { ...currentValues, ...newValues, projectKey };
      const projectKeyChanged = currentValues.projectKey !== projectKey;

      if (projectKeyChanged) {
        return { ...merged, issueType: '' };
      }
      return merged;
    });
  };

  // const handleSetupLater = async () => {
  //   const hasInputs = Object.keys(integration || {}).includes('inputs');

  //   await link({
  //     username: integration?.id || '',
  //     value: hasInputs ? 'setup' : 'waitlisted',
  //     config: {},
  //   });

  //   onComplete();
  //   onClose();
  // };

  const handleFileDrop = async (files: Files<'arrayBuffer'>) => {
    if (!integration?.id) return;

    try {
      const { file, content } = files[0];
      const fileUploadName = `imports/${integration.id}/${file.name}`;

      await uploadFile({
        name: fileUploadName,
        content,
      });

      await link({
        value: fileUploadName,
        config: {},
        username: `${integration.id}-import`,
      });

      track(AnalyticsEvent.INTEGRATION_CONNECTED, {
        integration: integration.id,
      });
      onComplete();
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const validateIntegrationConfig = async (preparedFormData: LinkAccount) => {
    setValidationErrors([]); // Clear previous errors

    if (!integration?.validate && !integration?.configuration) return true;

    const {
      isValid,
      templatesError: templatesErrorResponse,
      validationErrors: errors,
    } = await onValidateIntegration(
      {
        account: preparedFormData.key ?? '',
        config: preparedFormData.config,
        settings: preparedFormData.settings,
        templateId:
          step === 'configuration' ? String(configData.id) : undefined,
      },
      {
        suppressErrorToast: true, // Always suppress toast, show inline instead
      }
    );

    const templateError = configData.id
      ? templatesErrorResponse?.[configData.id]
      : undefined;

    if (step === 'configuration' && templateError) {
      setStep('permissions');
      return false;
    }

    if (templateError) return false;

    if (!isValid) {
      // Store validation errors for inline display
      if (errors && errors.length > 0) {
        setValidationErrors(errors);
      }
      track(AnalyticsEvent.INTEGRATION_VALIDATE_FAILURE, {
        integration: integration?.id || '',
      });
      return false;
    }

    track(AnalyticsEvent.INTEGRATION_VALIDATE_SUCCESS, {
      integration: integration?.id || '',
    });

    if (step === 'setup' && isValid && integration.configuration) {
      setStep('configuration');
      return false;
    }

    return true;
  };

  const prepareFormData = (): LinkAccount => {
    const setupDataRequestObj = parseFormData
      ? parseFormData(setupData)
      : setupData;
    const { displayName, ...restConfig } = configData;

    const projectName = projects.find(
      ({ key }) => key === restConfig.projectKey
    )?.name;

    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const currentTemplate = {
      ...restConfig,
      projectName,
      ...(integration?.transitions && transitionsData.openToClose.length > 0
        ? { transitions: transitionsData }
        : {}),
    };

    const hasMatchingTemplate = existingTemplates.some(
      template => template.id === restConfig.id
    );
    const newTemplates = isEditingMode
      ? hasMatchingTemplate
        ? existingTemplates.map(template =>
            template.id === restConfig.id ? currentTemplate : template
          )
        : [...existingTemplates, currentTemplate]
      : [...existingTemplates, currentTemplate];

    const config = convertObjectValuesToStrings(setupDataRequestObj);

    // Extract api_usage_limit from config so it goes to settings, not SSM secrets
    const apiUsageLimit = config.api_usage_limit;
    delete config.api_usage_limit;

    const buildSettings = (): LinkAccount['settings'] | undefined => {
      if (
        step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions'
      ) {
        return {
          displayName: String(displayName),
          templates: (newTemplates as JiraTemplate[]).map(value => {
            const { transitions, ...rest } = value;
            const converted = convertObjectValuesToStrings(rest);
            return transitions
              ? ({ ...converted, transitions } as ValidationCredentials & {
                  transitions: JiraTransitionConfig;
                })
              : converted;
          }) as (ValidationCredentials & {
            transitions?: JiraTransitionConfig;
          })[],
        };
      }

      if (setupDataRequestObj.severities) {
        return { severityThreshold: String(setupDataRequestObj.severities) };
      }

      if (apiUsageLimit && Number(apiUsageLimit) > 0) {
        const limit = Number(apiUsageLimit);
        const headroom = Math.ceil(limit * 0.1);
        return { api_usage: { limit, headroom } };
      }

      return undefined;
    };

    const result: LinkAccount = {
      username: integration?.overrideMemberName || integration?.id || '',
      value: String(setupDataRequestObj.value),
      config,
      key,
      settings: buildSettings(),
    };

    return result;
  };

  const handleLinkUnlinkIntegration = async (integrationData: LinkAccount) => {
    if (
      connectedIntegration &&
      !integration?.multiple &&
      !integration?.configuration
    ) {
      await unLink({
        username: connectedIntegration.member,
        value: connectedIntegration.value,
        config: connectedIntegration.config,
      });
    }
    if (isEditingMode) {
      await updateAccount(integrationData);
    } else {
      await link(integrationData);
    }
    track(AnalyticsEvent.INTEGRATION_CONNECTED, {
      integration: integrationData.username,
    });
    onComplete();
    onClose();
  };

  const handleLinkIntegration = async () => {
    setHasAttemptedSubmit(true);
    setValidationErrors([]); // Clear previous errors
    const inputs =
      step === 'setup'
        ? integration?.inputs
        : integration?.configuration?.inputs;

    // Client side validation
    if (!validateForm(inputs, step === 'setup' ? setupData : configData)) {
      setValidationErrors([
        {
          code: 'form_validation',
          title: 'Missing Required Fields',
          message: 'Please fill in all required fields before continuing.',
        },
      ]);
      return;
    }

    try {
      let integrationData = prepareFormData();
      if (!(await validateIntegrationConfig(integrationData))) {
        return;
      }

      await handleLinkUnlinkIntegration(integrationData as LinkAccount);
    } catch (error) {
      setValidationErrors([
        {
          code: 'unexpected_error',
          title: 'Connection Failed',
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred while connecting the integration.',
        },
      ]);
    }
  };

  // Map inputs to include error state (use processedInputs for dynamic options)
  // Memoized to prevent unnecessary re-renders of Inputs component
  const inputsWithErrors = useMemo(
    () =>
      processedInputs?.map(input => ({
        ...input,
        error: formErrors[input.name] ? ' ' : undefined, // Using space to trigger error state without showing message
      })) || [],
    [processedInputs, formErrors]
  );

  if (!integration) return null;

  const getModalFooterConfig = () => {
    const isLoading =
      isValidating ||
      [unLinkStatus, linkStatus, updateAccountStatus].includes('pending');

    return {
      hideCancelButton: false,
      text:
        step === 'permissions' && Object.keys(templatesError ?? {}).length > 0
          ? 'Try Again'
          : isLoading
            ? 'Validating...'
            : isEditingMode
              ? 'Save'
              : 'Connect',
      onClick:
        step === 'permissions' && Object.keys(templatesError ?? {}).length > 0
          ? () => setStep('configuration')
          : handleLinkIntegration,
      disabled: isLoading,
      className: 'px-6',
    };
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex w-full flex-row justify-between">
          <p>
            {integration.name} {step === 'setup' ? 'Setup' : 'Configuration'}
          </p>
          <img
            src={`/icons/${currentTheme}/${integration.logo}`}
            alt={integration.name}
            className="size-6"
          />
        </div>
      }
      hideFooter={integration.hideFooter}
      footer={getModalFooterConfig()}
    >
      {/* Inline validation error display */}
      {validationErrors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-red-500" />
            <div className="flex-1">
              {validationErrors.length === 1 ? (
                <>
                  <p className="text-sm font-medium text-red-500">
                    {validationErrors[0].title}
                  </p>
                  <p className="mt-1 text-sm text-red-400">
                    {validationErrors[0].message}
                  </p>
                  {validationErrors[0].hint && (
                    <p className="text-chariot-text-secondary mt-2 text-sm">
                      💡 {validationErrors[0].hint}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-red-500">
                    Validation Failed
                  </p>
                  <ul className="mt-2 space-y-2 text-sm text-red-400">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        <span className="font-medium">{error.title}:</span>{' '}
                        {error.message}
                        {error.hint && (
                          <p className="text-chariot-text-secondary mt-1 text-xs">
                            💡 {error.hint}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 'setup' && (
        <SetupSection
          integration={integration}
          inputsWithErrors={inputsWithErrors}
          setupData={setupData}
          onComplete={onComplete}
          defaultSetupValues={defaultSetupValues}
          handleSetupInputsChange={handleSetupInputsChange}
          handleFileDrop={handleFileDrop}
          isVulnerabilityScanner={isVulnerabilityScanner}
          scannerConfig={scannerConfig}
          onClose={onClose}
          onSwitchToFallback={
            integration?.fallbackIntegration && onSwitchIntegration
              ? () => {
                  const fallback =
                    AllIntegrations[integration.fallbackIntegration!];
                  if (fallback) {
                    onSwitchIntegration(fallback);
                  }
                }
              : undefined
          }
        />
      )}

      {(step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions') && (
        <ConfigurationSection
          step={step}
          setStep={setStep}
          integration={integration}
          templatesError={templatesError}
          projects={projects}
          formErrors={formErrors}
          configData={configData}
          setupData={setupData}
          defaultConfigValues={defaultConfigValues}
          handleConfigDataChange={handleConfigDataChange}
          searchProjects={handleSearchProjects}
          setProjects={setProjects}
          transitionsData={transitionsData}
          onTransitionsChange={setTransitionsData}
          accountKey={key}
        />
      )}
    </Modal>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook exported alongside components for convenience
export const useFormValidation = () => {
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const validateInputField = (
    input: IntegrationInput,
    value: string | number
  ): boolean => {
    if (input.required && !value) return false;
    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      return regex.test(value as string);
    }
    return true;
  };

  const validateForm = (
    inputs: IntegrationMeta['inputs'],
    data: Values
  ): boolean => {
    if (!inputs) return true;

    const newErrors: Record<string, boolean> = {};
    let isValid = true;

    (inputs || []).forEach(input => {
      if (!validateInputField(input, data[input.name])) {
        newErrors[input.name] = true;
        isValid = false;
      }
    });

    setFormErrors(newErrors);
    return isValid;
  };

  return {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  };
};

interface SetupSectionProps {
  integration: IntegrationMeta;
  onComplete: () => void;
  inputsWithErrors: IntegrationInput[];
  setupData: Values;
  defaultSetupValues?: Values;
  handleSetupInputsChange: (newValues: Values) => void;
  handleFileDrop: (files: Files<'arrayBuffer'>) => void;
  isVulnerabilityScanner?: boolean;
  scannerConfig: { accept: Accept } | null;
  onClose: () => void;
  onSwitchToFallback?: () => void;
}

export const SetupSection: React.FC<SetupSectionProps> = ({
  integration,
  onComplete,
  inputsWithErrors,
  setupData,
  defaultSetupValues,
  handleSetupInputsChange,
  handleFileDrop,
  isVulnerabilityScanner,
  scannerConfig,
  onClose,
  onSwitchToFallback,
}) => {
  const fallbackIntegration = integration.fallbackIntegration
    ? AllIntegrations[integration.fallbackIntegration]
    : null;

  return (
    <>
      {integration.help && (
        <div className="bg-chariot-background-secondary rounded-lg px-4 py-2">
          <p className="mb-1 text-sm font-bold">Need help?</p>
          <div className="flex flex-col space-y-2">
            <Link
              styleType="text"
              to={integration.help.href}
              target="_blank"
              rel="noopener noreferrer"
              buttonClass="p-0 hover:underline text-chariot-text-secondary font-base"
            >
              <InformationCircleIcon className="size-5" />
              <span className="underline">{integration.help.label}</span>
            </Link>
          </div>
        </div>
      )}

      {integration.Markup && (
        <div className="mt-4">
          <integration.Markup onComplete={onComplete} onClose={onClose} />
        </div>
      )}

      <div className="mt-4 space-y-4">
        <Inputs
          key={integration.id}
          inputs={inputsWithErrors}
          onChange={handleSetupInputsChange}
          values={setupData}
          defaultValues={defaultSetupValues}
        />

        {isVulnerabilityScanner && scannerConfig && (
          <>
            {inputsWithErrors.length > 0 &&
              (integration.inputs?.length ?? 0) > 0 && (
                <div className="relative mt-8">
                  <div className="relative flex justify-center">
                    <span className="bg-chariot-background-primary px-8 text-center font-semibold">
                      Or
                    </span>
                  </div>
                </div>
              )}
            <Dropzone
              type="arrayBuffer"
              className="border-chariot-stroke bg-chariot-background-primary rounded-lg border-2 border-dashed p-2"
              onFilesDrop={handleFileDrop}
              accept={scannerConfig.accept}
            >
              <div className="flex flex-col items-center p-2">
                <FilePlus2 className="text-chariot-text-secondary size-8" />
                <h3 className="m-0 mt-2 p-0 text-center text-lg font-medium">
                  Import {integration.name} Data
                </h3>
                <p className="text-chariot-text-primary mb-3 text-center text-sm">
                  Drag & Drop or Choose scan results to import
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-md p-2 py-0.5 rounded-full"
                  onClick={() => {}}
                ><PlusCircleIcon className="size-5" />Choose Files</Button>
              </div>
            </Dropzone>
          </>
        )}
      </div>

      {fallbackIntegration && onSwitchToFallback && (
        <div className="border-chariot-stroke mt-6 border-t pt-4">
          <p className="text-chariot-text-secondary text-sm">
            {integration.fallbackDescription ??
              'Try an alternative authentication method.'}{' '}
            <button
              type="button"
              onClick={onSwitchToFallback}
              className="text-chariot-brand-primary hover:underline"
            >
              {integration.fallbackLabel ?? 'Use alternative authentication →'}
            </button>
          </p>
        </div>
      )}
    </>
  );
};

interface ConfigurationSectionProps {
  step: 'configuration' | 'transitions' | 'permissions';
  setStep: (step: 'configuration' | 'transitions' | 'permissions') => void;
  integration: IntegrationMeta;
  templatesError: ValidateIntegrationErrorResponse['templates'];
  formErrors: Record<string, boolean>;
  configData: Values;
  setupData: Values;
  projects: JiraProject[];
  defaultConfigValues?: Values;
  handleConfigDataChange: (newValues: Values) => void;
  searchProjects: (searchTerm: string) => Promise<JiraProject[]>;
  setProjects: React.Dispatch<React.SetStateAction<JiraProject[]>>;
  transitionsData?: JiraTransitionConfig;
  onTransitionsChange?: (data: JiraTransitionConfig) => void;
  accountKey?: string;
}

export const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  step,
  setStep,
  integration,
  configData,
  setupData,
  templatesError,
  formErrors,
  defaultConfigValues,
  handleConfigDataChange,
  projects,
  searchProjects,
  setProjects: _setProjects,
  transitionsData,
  onTransitionsChange,
  accountKey,
}) => {
  // Check if this is a ticketing integration with project/issue type selection (Jira, Azure DevOps, etc.)
  const isTicketingIntegration = integration?.configuration?.inputs?.some(
    input => input.name === 'projectKey'
  );

  const getOptions = useCallback(
    (input: IntegrationInput, name: string) => {
      if (name === 'projectKey') {
        return projects.map(project => ({
          label: `${project.name} (${project.key})`,
          value: project.key,
        }));
      }
      if (name === 'issueType') {
        return projects
          .find(project => project.key === configData?.projectKey)
          ?.issueTypes.map(({ name }) => ({ label: name, value: name }));
      }
      return input.options;
    },
    [projects, configData]
  );

  const handleProjectKeyChange = (projectKey: string) => {
    handleConfigDataChange({
      ...configData,
      projectKey,
      issueType: '', // Reset issue type when project changes
    });
  };

  const getInputsConfiguration = useMemo(() => {
    // Satisfied when the dependency has a value: truthy (including non-empty string
    // e.g. projectKey) or checkbox true; not satisfied for empty or explicit 'false'.
    const isDepSatisfied = (val: unknown) => Boolean(val) && val !== 'false';

    const mapInputConfiguration = (
      input: IntegrationInput & {
        dependsOn?: string;
        hideTillDependsOn?: boolean;
      }
    ) => {
      const {
        dependsOn,
        hideTillDependsOn,
        hidden: hiddenInput,
        ...rest
      } = input;
      const options = getOptions(input, rest.name) || [];
      const depValue = dependsOn ? configData[dependsOn] : undefined;
      const depSatisfied = dependsOn ? isDepSatisfied(depValue) : true;
      const hidden = hideTillDependsOn ? !depSatisfied : hiddenInput;
      return {
        ...rest,
        disabled: dependsOn
          ? !depSatisfied
          : rest.type === Input.Type.SELECT
            ? options.length === 0
            : false,
        hidden,
        options,
        error: formErrors[input.name] ? ' ' : undefined,
      };
    };

    // Filter out projectKey for Jira - it will be rendered separately
    const inputs = (integration?.configuration?.inputs || []).filter(
      input => !(isTicketingIntegration && input.name === 'projectKey')
    );

    return (
      inputs.map(({ dependsOn, ...input }) =>
        mapInputConfiguration({ ...input, dependsOn })
      ) || []
    );
  }, [
    integration?.configuration?.inputs,
    configData,
    formErrors,
    getOptions,
    isTicketingIntegration,
  ]);

  // Determine if transitions tab should be shown
  const shouldShowTransitionsTab = Boolean(integration?.transitions);
  const availableTabs = shouldShowTransitionsTab
    ? configurationTabs
    : configurationTabs.filter(tab => tab.value !== 'transitions');

  return (
    <>
      {(step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions') &&
        integration.configuration?.permissions && (
          <Tabs
            tabs={availableTabs}
            selected={availableTabs.find(tab => tab.value === step) || null}
            onSelect={tab =>
              setStep(
                tab.value as 'configuration' | 'transitions' | 'permissions'
              )
            }
          />
        )}

      <Container
        className={cn(
          'mt-4 hidden space-y-4',
          step === 'configuration' && 'block'
        )}
      >
        {isTicketingIntegration && (
          <div className="space-y-1">
            <label
              htmlFor="projectKey"
              className="text-chariot-text-primary block text-sm font-medium"
            >
              Project
            </label>
            <JiraProjectSearch
              value={configData.projectKey as string}
              onChange={handleProjectKeyChange}
              searchProjects={searchProjects}
              initialProjects={projects}
              error={formErrors.projectKey ? ' ' : undefined}
              placeholder={
                integration?.projectSearchPlaceholder ??
                'Search for a project...'
              }
            />
          </div>
        )}
        <Inputs
          inputs={getInputsConfiguration}
          onChange={handleConfigDataChange}
          values={configData}
          defaultValues={defaultConfigValues}
        />
      </Container>

      {step === 'transitions' && integration?.transitions && (
        <Container className="mt-4">
          <TransitionsPanel
            accountKey={accountKey}
            projectKey={configData.projectKey as string}
            config={
              accountKey ? undefined : convertObjectValuesToStrings(setupData)
            }
            value={transitionsData}
            onChange={onTransitionsChange}
            disabled={!configData.projectKey}
          />
        </Container>
      )}

      {step === 'permissions' && (
        <Container className="mt-4 space-y-4">
          {Object.values(integration.configuration?.permissions ?? {})?.map(
            ({ key, name, description, permission }) => {
              const missingPermissions =
                templatesError?.[configData.id]?.missingPermissions ?? [];
              const havePermission = !missingPermissions.includes(key);
              return (
                <PermissionRow
                  key={key}
                  name={name}
                  description={description}
                  permission={permission}
                  havePermission={havePermission}
                />
              );
            }
          )}
        </Container>
      )}
    </>
  );
};

export const PermissionRow = ({
  name,
  description,
  permission,
  havePermission,
}: {
  name: string;
  description: string;
  permission: string;
  havePermission: boolean;
}) => {
  return (
    <div className="[&:not(:last-child)]:border-chariot-stroke flex items-center justify-between gap-2 pb-4 not-last:border-b">
      <div>
        <p
          className={cn(
            'text-sm font-bold',
            !havePermission && 'text-chariot-stroke-critical'
          )}
        >
          {name}
        </p>
        <p className="text-chariot-text-secondary text-xs">{description}</p>
      </div>
      <BaseTag
        className={cn(
          'h-6 px-2 py-1',
          !havePermission && 'text-chariot-stroke-critical'
        )}
      >
        {permission}
      </BaseTag>
    </div>
  );
};

const convertObjectValuesToStrings = (
  data: Record<string, string | number>
): ValidationCredentials =>
  Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {} as ValidationCredentials);

export default SetupModal;
